export interface UniversityProgram {
  name: string
  category: string
  degreeLevel?: string
}

export interface UniversityFeature {
  icon: string
  label: string
  description: string
  superscript?: string
}

export interface UniversityTestimonial {
  quote: string
  name: string
  program: string
}

export interface UniversityData {
  slug: string
  name: string
  shortName: string
  tagline: string
  heroTagline: string
  mission: string
  primaryColor: string
  secondaryColor: string
  ctaColor: string
  logoText: string
  logoUrl?: string
  heroImageUrl?: string
  phoneNumber?: string
  classesStartDate?: string
  programs: UniversityProgram[]
  degreeLevels: string[]
  features: UniversityFeature[]
  testimonial?: UniversityTestimonial
  mobileSection?: {
    heading: string
    description: string
  }
  accreditation: string
  founded: string
  type: string
}

export const universities: Record<string, UniversityData> = {
  'colorado-technical-university': {
    slug: 'colorado-technical-university',
    name: 'Colorado Technical University',
    shortName: 'CTU',
    tagline: 'Pursue a degree without putting your life on hold',
    heroTagline: 'Pursue a degree without putting your life on hold',
    mission:
      'Our mission is to provide industry-relevant higher education to a diverse student population through innovative technology and experienced faculty, enabling the pursuit of personal and professional goals.',
    primaryColor: '#991B1B',
    secondaryColor: '#1a1a1a',
    ctaColor: '#B91C1C',
    logoText: 'Colorado Technical University',
    logoUrl:
      'https://cdn.magicpatterns.com/uploads/6a2ATeMy5q3btLrv6d2QP1/CTU_logo._webp.webp',
    heroImageUrl:
      'https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=1400&h=800&fit=crop&q=80',
    phoneNumber: '855-204-0444',
    classesStartDate: 'May 5, 2026',
    degreeLevels: ["Associate's", "Bachelor's", "Master's", 'Doctoral'],
    programs: [
      { name: 'MBA', category: 'Business', degreeLevel: "Master's" },
      { name: 'Management', category: 'Business', degreeLevel: "Bachelor's" },
      { name: 'Accounting', category: 'Business', degreeLevel: "Bachelor's" },
      { name: 'Marketing', category: 'Business', degreeLevel: "Bachelor's" },
      {
        name: 'Project Management',
        category: 'Business',
        degreeLevel: "Master's",
      },
      { name: 'Computer Science', category: 'IT', degreeLevel: "Bachelor's" },
      { name: 'Cybersecurity', category: 'IT', degreeLevel: "Bachelor's" },
      {
        name: 'Software Engineering',
        category: 'IT',
        degreeLevel: "Bachelor's",
      },
      { name: 'Data Analytics', category: 'IT', degreeLevel: "Bachelor's" },
      { name: 'IT Management', category: 'IT', degreeLevel: "Master's" },
      {
        name: 'Healthcare Management',
        category: 'Healthcare',
        degreeLevel: "Master's",
      },
      {
        name: 'Health Informatics',
        category: 'Healthcare',
        degreeLevel: "Bachelor's",
      },
      {
        name: 'Criminal Justice',
        category: 'Criminal Justice',
        degreeLevel: "Bachelor's",
      },
      {
        name: 'Homeland Security',
        category: 'Criminal Justice',
        degreeLevel: "Bachelor's",
      },
      {
        name: 'Electrical Engineering',
        category: 'Engineering',
        degreeLevel: "Bachelor's",
      },
      {
        name: 'Computer Engineering',
        category: 'Engineering',
        degreeLevel: "Bachelor's",
      },
    ],
    features: [
      {
        icon: 'ArrowLeftRightIcon',
        label: 'Transfer Credits',
        description: 'Transfer in your eligible credits',
        superscript: '1',
      },
      {
        icon: 'FileCheckIcon',
        label: 'Financial Aid',
        description: 'Potential funding options available',
      },
      {
        icon: 'TimerIcon',
        label: 'CTU FastTrack™',
        description: 'Earn college credit for what you already know',
        superscript: '2',
      },
      {
        icon: 'AwardIcon',
        label: 'Academic Excellence',
        description: 'Study with instructors who are experienced professionals',
      },
    ],
    testimonial: {
      quote:
        'I had an awesome journey here at CTU. I chose CTU due the convenience of on line classes. This was such an amazing program. The professors were amazing and were all the steps of the way. I also loved my Success coach who was always available! CTU has the best online programs and was great experience!',
      name: 'Catherine J.',
      program: 'Master of Science in Healthcare Management 2024',
    },
    mobileSection: {
      heading: 'Access Classes from Your Phone',
      description:
        "CTU embraces new technologies that innovate and enhance the learning experience by offering increased connectivity and the freedom to choose where, when and how you study. That's why we are proud to bring you CTU Mobile, a unique educational experience in the palm of your hand. With our state-of-the-art mobile app, you can access your courses anywhere and work toward achieving your academic goals on your own time.",
    },
    accreditation: 'HLC Accredited',
    founded: '1965',
    type: 'Private, For-Profit',
  },
}

export const getUniversityBySlug = (slug: string) => universities[slug]
